<script setup lang="ts">
import type { BoxLayout, DisplayMode } from "@shopware/composables";
import { useTemplateRef } from "vue";
import {
  getProductFromPrice,
  getProductName,
  getProductRoute,
  getSmallestThumbnailUrl,
} from "@shopware/helpers";
import { ApiClientError } from "@shopware/api-client";
import type { Schemas } from "#shopware";
import { useGtmTracking } from '~/composables/useGtmTracking';
import { useNavigationTrailStore } from "@/stores/navigationTrail";

const { pushSuccess, pushError } = useNotifications();
const { t } = useI18n();
const { getErrorsCodes } = useCartNotification();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { resolveCartError } = useCartErrorParamsResolver();
const loading = ref<boolean>(false);

const { reviewCount } = useProductDetails();

const props = withDefaults(
  defineProps<{
    product: Schemas["Product"];
    layoutType?: BoxLayout;
    displayMode?: DisplayMode;
    variants?: boolean;
    isListingPage?: boolean;
  }>(),
  {
    layoutType: "standard",
    displayMode: "standard",
    variants: true,
  }
);
const { product } = toRefs(props);
const { addToCart, isInCart, count } = useAddToCart(product);
const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      loading.value = true;
      await addToWishlist();
      loading.value = false;
      return pushSuccess(
        t("product.messages.addedToWishlist", {
          p: props.product?.translated.name,
        })
      );
    } catch (error) {
      loading.value = false;
      // if (error instanceof ApiClientError) {
      const reason = error.details.errors?.[0]?.detail
        ? `Reason: ${error.details.errors?.[0]?.detail}`
        : "";
      return pushError(
        `${props.product?.translated.name} cannot be added to wishlist.\n${reason}`,
        {
          timeout: 5000,
        }
      );
      // }
    }
  } else {
    try {
      loading.value = true;
      await removeFromWishlist();
      loading.value = false;
    } catch (error) {
      loading.value = false;
    }
  }
};

const { trackAddToCart } = useGtmTracking();
const navigationTrailStore = useNavigationTrailStore();

const handleProductLinkClick = () => {
  if (props.isListingPage) {
    navigationTrailStore.markCategoryListingNavigation();
  }
};

const addToCartProxy = async () => {
  await addToCart();
  const errors = getErrorsCodes();

  for (const element of errors ?? []) {
    const { messageKey, params } = resolveCartError(element);
    pushError(t(`errors.${messageKey}`, params as Record<string, unknown>));
  }

  if (!errors.length) {
    trackAddToCart(product.value, 1);
    pushSuccess(
      t("cart.messages.addedToCart", { p: props.product?.translated.name })
    );
  }
};

const fromPrice = getProductFromPrice(props.product);
const { unitPrice, price, tierPrices, isListPrice } = useProductPrice(product);

const imageElement = useTemplateRef("imageElement");
const { height } = useElementSize(imageElement);

const DEFAULT_THUMBNAIL_SIZE = 10;
function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed(() => {
  return `${getSmallestThumbnailUrl(
    product.value.cover?.media
  )}?&height=${roundUp(height.value)}&fit=cover`;
});

const msbProductBadges = product.value?.extensions?.msbProductBadges ?? [];

const showroomBadgeDetails = msbProductBadges.filter(
  (badge) => badge.internalName === "showroomIcon"
);

const availableInHamburg = msbProductBadges.filter(
  (badge) => badge.internalName === "availableInHamburg"
);

const availableInBerlin = msbProductBadges.filter(
  (badge) => badge.internalName === "availableInBerlin"
);

const serieBadgeDetails = msbProductBadges.filter(
  (badge) => badge.internalName === "serie"
);
const excludedNames = [
  "showroomIcon",
  "serie",
  "availableInHamburg",
  "availableInBerlin",
];
const filteredBadges = msbProductBadges.filter(
  (badge) => !excludedNames.includes(badge.internalName)
);

const productLinkAriaLabel = computed(() => {
  const name =
    getProductName({ product: product.value }) ||
    product.value?.translated?.name ||
    "";
  return t("product.viewProductLink", { name });
});
</script>

<template>
  <div class="product-card group min-w-[280px] md:min-w-[0px]">
    <div
      class="relative z-20 overflow-hidden rounded-[15px] md:rounded-[20px] bg-light-grey aspect-[8/9] p-4 flex items-center justify-center"
    >
      <div
        class="absolute z-20 hover:z-30 top-[15px] left-[15px] md:top-[20px] right-[50px] flex flex-col items-start gap-[10px] 2xl:gap-3 3xl:gap-2 tall:gap-3"
      >
        <div class="group/tooltip relative" 
          v-if="filteredBadges.length > 0"
          v-for="(filteredBadge, index) in filteredBadges"
          :key="index">
          <div
            class="bg-[#6D6D36] text-white px-2 py-[2px] md:px-3 md:py-1 text-xs md:text-sm rounded-full group-hover/tooltip:text-grey-700 leading-4 text-center"
            :aria-label="$t('productStatusArialLable', { status: 'Set-angebot' })"
            :style="{
              backgroundColor: filteredBadge.backgroundColor,
              color: filteredBadge.badgeTextColor,
            }"
          >
            <span v-html="filteredBadge?.translated?.badgeText"></span>
          </div>
          <div
            v-if="filteredBadge?.translated?.tooltipText"
            class="absolute z-20 left-0 top-[35px] w-[250px] xl:w-[210px] 2xl:w-[210px] 3xl:w-[250px] tall:w-[280px] bg-white rounded-[10px] p-4 shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-300"
          >
            <div class="relative">
              <!-- Arrow -->
              <div
                class="absolute left-[-12px] top-1/2 -translate-y-1/2 w-3 h-3 bg-white transform rotate-45"
              ></div>
              <!-- Content -->
              <div
                class="bg-white relative z-10 text-gray-600 text-sm mb-3 leading-1 line-clamp-5 [&_p]:text-sm [&_ul]:text-sm [&_ol]:text-sm [&_li]:text-sm"
                v-html="filteredBadge?.translated?.tooltipText"
              ></div>
            </div>
          </div>
        </div>
        <div class="group/tooltip relative" v-if="serieBadgeDetails.length > 0">
          <button
            class="bg-transparent border-none text-grey-400 hover:text-grey-700 group-hover/tooltip:text-grey-700 translate-x-0 md:-translate-x-[60px] group-hover:translate-x-0 transition-all duration-300"
            :aria-label="$t('compareProductAriaLabel')"
          >
            <i
              class="icon-off text-lg 2xl:text-xl 3xl:text-2xl"
              aria-hidden="true"
            ></i>
          </button>
          <div
            v-if="serieBadgeDetails[0]?.translated?.tooltipText"
            class="absolute z-20 left-0 top-[35px] w-[250px] xl:w-[210px] 2xl:w-[210px] 3xl:w-[250px] tall:w-[280px] bg-white rounded-[10px] p-4 shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-300"
          >
            <div class="relative">
              <!-- Arrow -->
              <div
                class="absolute left-[-12px] top-1/2 -translate-y-1/2 w-3 h-3 bg-white transform rotate-45"
              ></div>
              <!-- Content -->
              <div
                class="bg-white relative z-10 text-gray-600 text-sm mb-3 leading-1 line-clamp-5 [&_p]:text-sm [&_ul]:text-sm [&_ol]:text-sm [&_li]:text-sm"
                v-html="serieBadgeDetails[0]?.translated?.tooltipText"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <NuxtLink
        :to="formatLink(getProductRoute(product))"
        class="block w-full h-full relative z-10 bg-light-grey "
        :aria-label="productLinkAriaLabel"
        @click="handleProductLinkClick"
      >
        <SharedImage
          v-if="product?.customFields?.listingImage"
          loading="lazy"
          :src="product?.customFields?.listingImage?.url"
          :alt="getProductName({ product }) || $t('mysofbed')"
          :height="product?.customFields?.listingImage?.metaData?.height"
          :width="product?.customFields?.listingImage?.metaData?.width"
          imgClass="w-full max-h-full max-w-full object-contain mix-blend-multiply transition-opacity duration-300 absolute inset-0 m-auto"
          :class="{
            'group-hover:opacity-0': product?.customFields?.listingImage,
          }"
          :sizes="{
            desktop: '387x164',
            tablet: '291x124',
            mobile: '280x119',
          }"
          format="webp"
          :mimeType="product?.customFields?.listingImage?.mimeType"
        />
        <SharedImage
          v-else
          loading="lazy"
          :src="product?.cover?.media?.url"
          :alt="getProductName({ product }) || $t('mysofbed')"
          :height="product?.cover?.media?.metaData?.height"
          :width="product?.cover?.media?.metaData?.width"
          imgClass="w-full max-h-full max-w-full object-contain mix-blend-multiply transition-opacity duration-300 absolute inset-0 m-auto"
          :class="{ 'group-hover:opacity-0': product.customFields?.hoverImage }"
          :sizes="{
            desktop: '387x164',
            tablet: '291x124',
            mobile: '280x119',
          }"
          format="webp"
          :mimeType="product?.cover?.media?.mimeType"
        />

        <SharedImage
          loading="lazy"
          v-if="product.customFields?.hoverImage"
          :src="product.customFields?.hoverImage?.url"
          :alt="product.customFields?.hoverImage?.alt ?? $t('mysofbed')"
          :height="product.customFields?.hoverImage?.metaData?.height"
          :width="product.customFields?.hoverImage?.metaData?.width"
          imgClass="w-full max-h-full max-w-full object-contain mix-blend-multiply transition-opacity duration-300 absolute inset-0 m-auto opacity-0 group-hover:opacity-100"
          :sizes="{
            desktop: '387x164',
            tablet: '291x124',
            mobile: '280x119',
          }"
          format="webp"
          :mimeType="product?.customFields?.hoverImage?.mimeType"
        />
      </NuxtLink>

      <div
        class="absolute z-20 hover:z-30 right-[15px] top-[15px] md:right-[20px] md:top-[20px] flex flex-col gap-[10px] 2xl:gap-2 3xl:gap-1 tall:gap-2 translate-x-0 md:translate-x-[60px] group-hover:translate-x-0 transition-transform duration-300"
      >
        <div class="group/tooltip" v-if="showroomBadgeDetails.length > 0">
          <button
            class="bg-transparent border-none hover:text-grey-700 group-hover:text-grey-700 transition-all duration-300"
            :aria-label="$t('viewInShowroom')"
          >
            <i
              class="icon-showroom text-grey-400 text-lg 2xl:text-xl 3xl:text-2xl block group-hover/tooltip:hidden transition-all duration-300 p-2 -mt-2 -mr-2"
              aria-hidden="true"
            ></i>
            <i
              class="icon-showroom-fill text-grey-700 text-lg 2xl:text-xl 3xl:text-2xl hidden group-hover/tooltip:block transition-all duration-300 p-2 -mt-2 -mr-2"
              aria-hidden="true"
            ></i>
          </button>

          <!-- Update tooltip -->
          <div
            v-if="(availableInHamburg.length > 0 && availableInHamburg[0]?.translated?.tooltipText) || (availableInBerlin.length > 0 && availableInBerlin[0]?.translated?.tooltipText) || (availableInHamburg.length > 0 && availableInBerlin.length > 0 && showroomBadgeDetails[0]?.translated?.tooltipText)"
            class="absolute z-20 right-0 top-[35px] w-[250px] xl:w-[210px] 2xl:w-[210px] 3xl:w-[250px] tall:w-[280px] bg-white rounded-[10px] p-4 shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-300"
          >
            <div class="relative">
              <!-- Arrow -->
              <div
                class="absolute right-[-12px] top-1/2 -translate-y-1/2 w-3 h-3 bg-white transform rotate-45"
              ></div>

              <!-- Content -->
              <template v-if="availableInHamburg.length > 0 && availableInBerlin.length > 0">
                <div
                  class="bg-white relative z-10"
                  v-html="showroomBadgeDetails[0]?.translated?.tooltipText"
                ></div>
              </template> 
              <div
                v-else-if="availableInHamburg.length > 0"
                class="bg-white relative z-10"
                v-html="availableInHamburg[0]?.translated?.tooltipText"
              ></div>
              <div
                v-else-if="availableInBerlin.length > 0"
                class="bg-white relative z-10 mt-2"
                v-html="availableInBerlin[0]?.translated?.tooltipText"
              ></div>
            </div>
          </div>
        </div>
        <ClientOnly>
          <button
            class="bg-transparent border-none text-grey-400 hover:text-grey-700 transition-all duration-300"
            :aria-label="$t('addToWishList')"
            @click="toggleWishlistProduct"
            :disabled="loading"
          >
            <i
              class="text-lg 2xl:text-xl 3xl:text-2xl p-2 -mt-2 -mr-2"
              :class="{
                'icon-heart-fill': isInWishlist,
                'icon-heart-outline': !isInWishlist,
                'animate-pulse cursor-progress': loading,
              }"
              aria-hidden="true"
            ></i>
          </button>
        </ClientOnly>
      </div>

      <div
        class="absolute inset-x-0 bottom-0 text-center translate-y-0 md:translate-y-full group-hover:translate-y-0 transition-transform duration-300 p-4 md:p-6 z-20"
      >
        <NuxtLink
          :to="formatLink(getProductRoute(product))"
          class="text-grey-700 font-marcellus text-base 3xl:text-md underline hover:text-primary transition-colors duration-300"
          :aria-label="productLinkAriaLabel"
          @click="handleProductLinkClick"
        >
          {{ $t("product.viewDetails") }}
        </NuxtLink>
        <div
          v-if="
            product.customFields?.configuratorColors?.colors &&
            product.customFields?.configuratorColors?.total > 0
          "
          class="flex justify-center gap-1 md:gap-2 mt-3 md:mt-4 h-4 md:h-5 3xl:h-6"
          :aria-label="$t('availableColors')"
        >
          <span
            v-for="(
              color, index
            ) in product.customFields?.configuratorColors?.colors.slice(0, 6)"
            :key="index"
            class="w-4 h-4 md:w-5 md:h-5 3xl:w-6 3xl:h-6 rounded-full bg-gray-500 flex-shrink-0"
            aria-hidden="true"
            :style="{ backgroundColor: color }"
          ></span>

          <span
            v-if="product.customFields?.configuratorColors?.total > 6"
            class="text-grey-700 leading-tight md:leading-normal ml-1 text-xs md:text-base"
            >+{{
              product.customFields?.configuratorColors?.total - 6
            }}</span
          >
        </div>
      </div>
    </div>
    <div class="product-info mt-5 text-center">
      <h3
        class="text-base md:text-md 3xl:text-lg font-medium overflow-hidden line-clamp-2 md:line-clamp-3 leading-[28px]"
      >
        <NuxtLink
          :to="formatLink(getProductRoute(product))"
          class="text-grey-700 hover:text-primary transition-colors duration-300"
          :aria-label="productLinkAriaLabel"
          @click="handleProductLinkClick"
        >
          {{ getProductName({ product }) }}
        </NuxtLink>
      </h3>
      <div class="price-info mt-1" :aria-label="$t('productPricing')">
        <SharedListingProductPrice
          :product="product"
          data-testid="product-box-product-price"
        />
      </div>
      <div
        v-if="product?.ratingAverage && isListingPage"
        class="flex justify-center"
      >
        <SharedProductRating
          class="mt-2 md:scale-[85%]"
          :product-rating="product?.ratingAverage"
          :review-count="product?.productReviewsCount ?? 0"
        />
      </div>
    </div>
  </div>
</template>
